import React, { useState } from 'react';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Backdrop,
  CircularProgress
} from '@material-ui/core';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { v4 as uuidv4 } from 'uuid';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import SaveIcon from '@material-ui/icons/Save';
import Controls from '../../components/controls/Controls';
import {
  baseUrl,
  userDept,
  userStatuses,
  userType,
  Country,
  employers
} from '../../utils/constants';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    border: '1px solid rgba(0, 0, 0, .125)',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0
    }
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary
  },
  color: {
    backgroundColor: '#f5f5f5'
  },
  newButton: {
    position: 'absolute',
    right: '10px'
  },
  spacing: {
    marginBottom: theme.spacing(2)
  }
}));

export default function CreateUser() {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: 'success' });
  const [status, setStatus] = useState('');
  const [dept, setDept] = useState('');
  const [expanded, setExpanded] = React.useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const UserSchema = Yup.object().shape({
    userName: Yup.string().required('User Name is required'),
    password: Yup.string().required('Password is required'),
    firstName: Yup.string().required('First Name is required'),
    emailAddress: Yup.string().email('Invalid email').required('Email Address is required'),
    status: Yup.number().required('Status is required'),
    countryRefId: Yup.number().required('Country Reference ID is required')
  });

  const now = new Date().toISOString();
  const initialValues = {
    userName: '',
    password: '',
    countryRefId: 1,
    firstName: '',
    surname: '',
    idNumber: '',
    emailAddress: '',
    cellNumber: '',
    status: 1,
    lastLogIn: now,
    passwordPolicy: 0,
    province: 11,
    lastPasswordChange: now,
    systemLanguage: 0,
    userType: 2,
    passwordToken: null,
    description: '',
    supervisorRefId: 0,
    extension: '',
    oldRefId: 0,
    dept: 2,
    refId: 0
  };

  const formik = useFormik({
    initialValues,
    validationSchema: UserSchema,
    onSubmit: (values) => {
      console.log('User data:', values);
      saveUser(values);
    }
  });

  const saveUser = (values) => {
    console.log('Payload to be sent:', JSON.stringify(values));
    setLoading(true);
    fetch(`${baseUrl}/api/UserManagement/AddUser`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(values)
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(
        (result) => {
          notifyPopup('User Saved Successfully!', 'success');
          setLoading(false);
          navigate('/settings/configusers');
        },
        (error) => {
          notifyPopup(`Error in Saving User!: ${error.message}`, 'error');
          setLoading(false);
        }
      )
      .catch((error) => {
        notifyPopup(`Error in Saving User!: ${error.message}`, 'error');
        setLoading(false);
      });
  };

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleLoadingClose = () => {
    setLoading(false);
  };

  const notifyPopup = (notifyMessage, notifyType) => {
    setNotify({
      isOpen: true,
      message: notifyMessage,
      type: notifyType
    });
  };

  return (
    <Card>
      <CardContent>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
          onClick={handleLoadingClose}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
            <Typography variant="subtitle1" gutterBottom>
              Create User
            </Typography>
            <Grid container spacing={2} justifyContent="center" alignItems="center">
              <Grid item xs={12} sm={6}>
                <Controls.Input name="userName" label="User Name" autoComplete="new-password" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controls.Input
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controls.Input name="firstName" label="First Name" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controls.Input name="surname" label="Surname" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controls.Input name="idNumber" label="ID Number" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controls.Input name="emailAddress" label="Email Address" type="email" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controls.Input name="cellNumber" label="Cell Number" />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controls.SelectJson
                  name="status"
                  label="Status"
                  defaultValue=""
                  options={userStatuses}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controls.SelectJson
                  name="dept"
                  label="Department"
                  defaultValue=""
                  options={userDept}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controls.SelectJson name="province" label="Province" options={employers} />
              </Grid>
              <Grid item xs={12} sm={9}>
                <Controls.Input name="description" label="Description" />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Controls.SelectJson
                  name="userType"
                  label="userType"
                  defaultValue=""
                  options={userType}
                />
              </Grid>
              <Grid item xs={12} className={classes.spacing} />
            </Grid>
            <div className={classes.root}>
              <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                  className={classes.color}
                >
                  <Typography className={classes.heading}>Auto Populated Fields</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2} marginTop={2}>
                    {/* <Grid item xs={12} sm={6}>
                        <Paper variant="outlined" margin={10}> */}
                    <Grid item xs={12} sm={4}>
                      <Controls.Input name="extension" label="Extension" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controls.Input name="oldRefId" label="Old Reference ID" type="number" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controls.DateTime name="lastLogIn" label="Last Login" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controls.DateTime name="lastPasswordChange" label="Last Password Change" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controls.Input name="systemLanguage" label="System Language" type="number" />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Controls.SelectJson
                        name="countryRefId"
                        label="Country"
                        defaultValue=""
                        options={Country}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controls.Input
                        name="supervisorRefId"
                        label="Supervisor Reference ID"
                        type="number"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controls.Input name="passwordToken" label="Password Token" />
                    </Grid>
                    {/* </Paper>
                      </Grid> */}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </div>
            <Grid container spacing={2} justifyContent="center" alignItems="center">
              <Grid item xs={12} sm={4}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </CardContent>
    </Card>
  );
}
